// Pure layout logic for the dashboard renderer (design doc §2). Markdown
// tiles are section BOUNDARIES, not grid content: the renderer walks
// `layout.rows` once, in order, and partitions them into sections at every
// markdown tile. `ChartTile.width` is read as a proportion of a 12-column row
// (`columnSpans`). `height` is ignored.
import type { ChartTile, DashboardLayout, LayoutTile, MarkdownTile } from "@bi/shared/types";

export function isMarkdownTile(tile: LayoutTile): tile is MarkdownTile {
  return "markdown" in tile;
}

export function isChartTile(tile: LayoutTile): tile is ChartTile {
  return "chart_uuid" in tile;
}

export interface DashboardSection {
  /** The FULL markdown SOURCE of the tile that opened this section, or
   *  `undefined` for content before the first markdown tile (a dashboard can and
   *  does start with chart rows before any heading). A tile is a heading plus
   *  prose: render it through `components/markdown.tsx`, never as a bare string. */
  markdown?: string;
  /** Every chart tile between this heading and the next, grouped by the
   *  LayoutRow it came from (each inner array is one authored row's chart
   *  tiles — a row with no chart tiles, e.g. one that was ALL markdown, is
   *  simply absent here rather than an empty array). */
  chartRows: ChartTile[][];
}

/**
 * Partitions `layout.rows` into sections at every markdown tile. A markdown
 * tile mid-row (rare, but the type allows a row to mix tile kinds) closes
 * the section AT that row: whatever chart tiles preceded it in the same row
 * stay in the section that's closing, whatever chart tiles follow it open
 * the next one.
 */
export function toSections(layout: DashboardLayout): DashboardSection[] {
  const sections: DashboardSection[] = [];
  let current: DashboardSection = { chartRows: [] };

  const pushRowCharts = (charts: ChartTile[]) => {
    if (charts.length > 0) current.chartRows.push(charts);
  };

  for (const row of layout.rows) {
    let rowCharts: ChartTile[] = [];
    for (const tile of row.tiles) {
      if (isMarkdownTile(tile)) {
        pushRowCharts(rowCharts);
        rowCharts = [];
        // Close the current section and open the next, headed by this
        // tile — even when the closing section carries nothing (a
        // markdown-only dashboard is legitimate).
        sections.push(current);
        current = { markdown: tile.markdown, chartRows: [] };
      } else if (isChartTile(tile)) {
        rowCharts.push(tile);
      }
    }
    pushRowCharts(rowCharts);
  }
  sections.push(current);

  // Drop a contentless LEADING section only (no heading AND no charts) —
  // nothing came before the dashboard's first markdown tile, so it's pure
  // noise. Never drop a LATER section this way: a markdown-only closing
  // section (e.g. a trailing "Notes" heading with no charts of its own) is
  // legitimate authored content, not an artifact of this partitioning.
  const first = sections[0];
  if (
    sections.length > 1 &&
    first !== undefined &&
    first.markdown === undefined &&
    first.chartRows.length === 0
  ) {
    sections.shift();
  }
  return sections;
}

export type ChartSizeClass = "sm" | "md" | "lg";

/** Everything `sizeClass` and `columnSpans` read off a tile. Not `ChartTile`:
 *  the studio canvas lays markdown and chart tiles out on the same twelve
 *  columns, and must reuse `columnSpans` rather than keep a second copy. */
export interface SizedTile {
  width: number;
}

/** `ChartTile.width` (1-12, Superset grid units) collapsed to three coarse
 *  buckets. This is no longer how a tile is SIZED (see `columnSpans`) — it
 *  survives as the coarse "how much room does this tile have" question the
 *  card itself asks, e.g. how tall to draw its loading skeleton. `height` is
 *  unused entirely: it's Superset's own internal relative unit, not a CSS
 *  pixel value, and every primitive here already has a sane intrinsic size. */
export function sizeClass(tile: SizedTile): ChartSizeClass {
  if (tile.width <= 4) return "sm";
  if (tile.width <= 8) return "md";
  return "lg";
}

/** The grid this renderer lays a row out on. Superset's own unit, kept because
 *  the authored widths are written in it. */
export const GRID_COLUMNS = 12;

/**
 * The column span for each tile in one authored row, NORMALIZED so the row
 * always fills the grid exactly.
 *
 * Authored rows often do not sum to 12 (`validate.py` warns but does not fail).
 * Normalizing keeps each row's authored ratios (e.g. a two-thirds chart beside a
 * one-third stat column) while a row summing to 10 or 14 still fills its width.
 * A tile never collapses below one column, and any rounding remainder lands on
 * the widest tile, where a column of slop is least visible.
 */
export function columnSpans(tiles: readonly SizedTile[]): number[] {
  if (tiles.length === 0) return [];
  // More tiles than columns: one each, and the grid wraps them. Authored rows
  // never get near this, but a span of 0 would make a tile vanish.
  if (tiles.length >= GRID_COLUMNS) return tiles.map(() => 1);
  const total = tiles.reduce((sum, tile) => sum + Math.max(tile.width, 1), 0);
  const spans = tiles.map((tile) =>
    Math.max(1, Math.round((Math.max(tile.width, 1) / total) * GRID_COLUMNS)),
  );
  let remainder = GRID_COLUMNS - spans.reduce((sum, span) => sum + span, 0);
  // Settle the rounding on the widest tile (ties to the leftmost), one column
  // at a time so no tile is pushed below 1 while giving columns back.
  while (remainder !== 0) {
    let target = 0;
    for (let i = 1; i < spans.length; i += 1) {
      if ((spans[i] as number) > (spans[target] as number)) target = i;
    }
    if (remainder > 0) {
      spans[target] = (spans[target] as number) + 1;
      remainder -= 1;
    } else {
      if ((spans[target] as number) <= 1) break;
      spans[target] = (spans[target] as number) - 1;
      remainder += 1;
    }
  }
  return spans;
}
